export const ANSI_COLOR_NAMES = [
  'black',
  'red',
  'green',
  'yellow',
  'blue',
  'magenta',
  'cyan',
  'white',
  'brightBlack',
  'brightRed',
  'brightGreen',
  'brightYellow',
  'brightBlue',
  'brightMagenta',
  'brightCyan',
  'brightWhite',
] as const;

export type AnsiColorName = (typeof ANSI_COLOR_NAMES)[number];

export type AnsiStyle = {
  foreground: AnsiColorName | null;
  bold: boolean;
  dim: boolean;
  italic: boolean;
  underline: boolean;
};

export type AnsiTextRange = {
  from: number;
  to: number;
};

export type AnsiStyledRange = AnsiTextRange & {
  style: AnsiStyle;
};

export type AnsiDocumentLayout = {
  escapeRanges: AnsiTextRange[];
  styledRanges: AnsiStyledRange[];
};

type AnsiEscapeSequence = AnsiTextRange & {
  sgrCodes: number[] | null;
};

const ESCAPE_CHARACTER = String.fromCharCode(27);
const ANSI_CONTROL_SEQUENCE_SOURCE = `${ESCAPE_CHARACTER}\\[[0-9;?]*[A-Za-z]`;
const SGR_FINAL_CHARACTER = 'm';
const EXTENDED_FOREGROUND_CODE = 38;
const EXTENDED_BACKGROUND_CODE = 48;
const EXTENDED_COLOR_PALETTE_MODE = 5;
const EXTENDED_COLOR_RGB_MODE = 2;
const RGB_COMPONENT_COUNT = 3;

const DEFAULT_ANSI_STYLE: AnsiStyle = {
  foreground: null,
  bold: false,
  dim: false,
  italic: false,
  underline: false,
};

const SGR_ATTRIBUTE_UPDATES: Partial<Record<number, Partial<AnsiStyle>>> = {
  1: { bold: true },
  2: { dim: true },
  3: { italic: true },
  4: { underline: true },
  22: { bold: false, dim: false },
  23: { italic: false },
  24: { underline: false },
  39: { foreground: null },
};

const createControlSequencePattern = (): RegExp =>
  new RegExp(ANSI_CONTROL_SEQUENCE_SOURCE, 'g');

const parseSgrCodes = (sequence: string): number[] | null => {
  if (!sequence.endsWith(SGR_FINAL_CHARACTER)) {
    return null;
  }

  return sequence
    .slice(2, -1)
    .split(';')
    .map((parameter) => {
      const code = Number.parseInt(parameter, 10);

      return Number.isNaN(code) ? 0 : code;
    });
};

const colorAt = (index: number): AnsiColorName | null =>
  ANSI_COLOR_NAMES[index] ?? null;

const applySgrCode = ({
  style,
  code,
}: {
  style: AnsiStyle;
  code: number;
}): AnsiStyle => {
  if (code === 0) {
    return DEFAULT_ANSI_STYLE;
  }

  const attributeUpdate = SGR_ATTRIBUTE_UPDATES[code];

  if (attributeUpdate) {
    return { ...style, ...attributeUpdate };
  }

  if (code >= 30 && code <= 37) {
    return { ...style, foreground: colorAt(code - 30) };
  }

  if (code >= 90 && code <= 97) {
    return { ...style, foreground: colorAt(code - 90 + 8) };
  }

  return style;
};

const applySgrCodes = ({
  style,
  codes,
}: {
  style: AnsiStyle;
  codes: readonly number[];
}): AnsiStyle => {
  const [code, ...rest] = codes;

  if (code === undefined) {
    return style;
  }

  if (code === EXTENDED_FOREGROUND_CODE || code === EXTENDED_BACKGROUND_CODE) {
    const [mode, ...afterMode] = rest;
    const paletteIndex = afterMode[0];
    const nextStyle =
      code === EXTENDED_FOREGROUND_CODE &&
      mode === EXTENDED_COLOR_PALETTE_MODE &&
      paletteIndex !== undefined
        ? { ...style, foreground: colorAt(paletteIndex) ?? style.foreground }
        : style;
    const consumedCount =
      mode === EXTENDED_COLOR_PALETTE_MODE
        ? 1
        : mode === EXTENDED_COLOR_RGB_MODE
          ? RGB_COMPONENT_COUNT
          : 0;

    return applySgrCodes({
      style: nextStyle,
      codes: afterMode.slice(consumedCount),
    });
  }

  return applySgrCodes({ style: applySgrCode({ style, code }), codes: rest });
};

const isDefaultAnsiStyle = (style: AnsiStyle): boolean =>
  style.foreground === null &&
  !style.bold &&
  !style.dim &&
  !style.italic &&
  !style.underline;

export const stripAnsiEscapes = (text: string): string =>
  text.replace(createControlSequencePattern(), '');

export const parseAnsiDocument = (text: string): AnsiDocumentLayout => {
  const escapes: AnsiEscapeSequence[] = Array.from(
    text.matchAll(createControlSequencePattern()),
    (match) => ({
      from: match.index,
      to: match.index + match[0].length,
      sgrCodes: parseSgrCodes(match[0]),
    }),
  );

  const stylesAfterEscape = escapes.reduce<AnsiStyle[]>((styles, escape) => {
    const previousStyle = styles.at(-1) ?? DEFAULT_ANSI_STYLE;

    styles.push(
      escape.sgrCodes
        ? applySgrCodes({ style: previousStyle, codes: escape.sgrCodes })
        : previousStyle,
    );

    return styles;
  }, []);

  const styledRanges = escapes
    .map((escape, index) => ({
      from: escape.to,
      to: escapes[index + 1]?.from ?? text.length,
      style: stylesAfterEscape[index] ?? DEFAULT_ANSI_STYLE,
    }))
    .filter(
      (range) => range.to > range.from && !isDefaultAnsiStyle(range.style),
    );

  return {
    escapeRanges: escapes.map(({ from, to }) => ({ from, to })),
    styledRanges,
  };
};
